import { BaseComponent } from '../lib/BaseComponent.js';
import { api, Extinguisher, Inspection } from '../lib/api.js';
import { formatDate } from '../lib/dates.js';
import { router } from '../lib/router.js';
import { buildAppNav, APP_NAV_STYLES } from '../lib/app-nav.js';
import { Toast } from './shared/Toast.js';
import { EXTINGUISHER_TYPES, EXTINGUISHER_AGENTS } from '../lib/extinguisher-types.js';
import '../components/shared/Modal.js';

/**
 * Extinguisher Detail Page Component
 * Shows full extinguisher information and inspection history
 */
export class ExtinguisherDetailPage extends BaseComponent {
  private isLoading = true;
  private extinguisher: Extinguisher | null = null;
  private inspections: Inspection[] = [];
  private errorMessage = '';
  private extinguisherId: string = '';

  connectedCallback(): void {
    // Get extinguisher ID from URL
    this.extinguisherId = router.getParam('id') || '';
    super.connectedCallback();
  }

  protected async render(): Promise<void> {
    this.addStyles(this.getCommonStyles());
    this.addStyles(this.getStyles());

    const container = this.createElement('div', { className: 'detail-container' });

    // Header with navigation
    const header = this.createElement('header', { className: 'detail-header' });
    const headerContent = this.createElement('div', { className: 'header-content' });

    const backBtn = this.createElement('button', { className: 'back-btn' }, '← Back to List');
    backBtn.addEventListener('click', () => router.navigate('/extinguishers'));
    headerContent.appendChild(backBtn);

    const logo = this.createElement('h1', { className: 'logo' }, 'FireGuard');
    headerContent.appendChild(logo);

    const nav = buildAppNav((t, p, txt) => this.createElement(t as any, p as any, txt as any), '/extinguishers', () => this.handleLogout());
    headerContent.appendChild(nav);

    header.appendChild(headerContent);
    container.appendChild(header);

    // Main content
    const main = this.createElement('main', { className: 'detail-main' });

    // Loading state
    if (this.isLoading) {
      const loading = this.createElement('div', { className: 'loading' }, 'Loading extinguisher details...');
      main.appendChild(loading);
      container.appendChild(main);
      this.shadow.appendChild(container);

      // Fetch data
      await this.loadExtinguisherData();
      return;
    }

    // Error state
    if (this.errorMessage || !this.extinguisher) {
      const error = this.createElement('div', { className: 'error-message' }, this.errorMessage || 'Extinguisher not found');
      main.appendChild(error);
      container.appendChild(main);
      this.shadow.appendChild(container);
      return;
    }

    // Extinguisher details
    const detailsSection = this.createDetailsSection();
    main.appendChild(detailsSection);

    // Inspection history
    const historySection = this.createHistorySection();
    main.appendChild(historySection);

    // Action buttons
    const actions = this.createActionsSection();
    main.appendChild(actions);

    container.appendChild(main);
    this.shadow.appendChild(container);
  }

  private createDetailsSection(): HTMLElement {
    if (!this.extinguisher) return this.createElement('div', {});

    const section = this.createElement('section', { className: 'section details-section' });
    const sectionHeader = this.createElement('div', { className: 'section-header' });
    const title = this.createElement('h2', {}, this.extinguisher.qr_code);
    const badge = this.createElement('span', {
      className: `status-badge status-${this.extinguisher.compliance_status}`,
    }, this.extinguisher.compliance_status.toUpperCase());
    sectionHeader.appendChild(title);
    sectionHeader.appendChild(badge);
    section.appendChild(sectionHeader);

    // Details grid
    const grid = this.createElement('div', { className: 'details-grid' });

    const details = [
      { label: 'QR Code', value: this.extinguisher.qr_code },
      { label: 'Fire class', value: this.extinguisher.extinguisher_type },
      // The class does not say how the unit is checked. CO2 has no gauge and is
      // weighed; a dry-chemical unit of the same class has one.
      { label: 'Agent', value: this.extinguisher.agent || 'Not recorded' },
      { label: 'Size', value: `${this.extinguisher.size_lbs} lbs` },
      { label: 'Room/Area', value: this.extinguisher.room_area || 'N/A' },
      { label: 'Status', value: this.extinguisher.status.toUpperCase() },
      { label: 'Install Date', value: formatDate(this.extinguisher.install_date) },
      {
        label: 'Last Monthly Inspection',
        value: this.extinguisher.last_monthly_inspection
          ? formatDate(this.extinguisher.last_monthly_inspection)
          : 'Never'
      },
      {
        label: 'Next Monthly Inspection',
        value: this.extinguisher.next_monthly_inspection
          ? formatDate(this.extinguisher.next_monthly_inspection)
          : 'Not scheduled'
      },
      { label: 'Last Annual Maintenance', value: this.extinguisher.last_annual_maintenance ? formatDate(this.extinguisher.last_annual_maintenance) : 'Not recorded' },
      { label: 'Next Annual Maintenance', value: this.extinguisher.next_annual_maintenance ? formatDate(this.extinguisher.next_annual_maintenance) : 'Not scheduled' },
      { label: 'Six-Year Internal Exam', value: this.extinguisher.last_six_year_exam ? `${formatDate(this.extinguisher.last_six_year_exam)} · next ${formatDate(this.extinguisher.next_six_year_exam)}` : 'Not recorded' },
      { label: 'Hydrostatic Test', value: this.extinguisher.last_hydrostatic_test ? `${formatDate(this.extinguisher.last_hydrostatic_test)} · next ${formatDate(this.extinguisher.next_hydrostatic_test)}` : 'Not recorded' },
    ];

    details.forEach(({ label, value }) => {
      const item = this.createElement('div', { className: 'detail-item' });
      const labelEl = this.createElement('div', { className: 'detail-label' }, label);
      const valueEl = this.createElement('div', { className: 'detail-value' }, value);
      item.appendChild(labelEl);
      item.appendChild(valueEl);
      grid.appendChild(item);
    });

    section.appendChild(grid);

    // Compliance info
    if (this.extinguisher.compliance_status !== 'compliant') {
      const alert = this.createElement('div', {
        className: `compliance-alert alert-${this.extinguisher.compliance_status}`,
      });
      const alertIcon = this.createElement('span', { className: 'alert-icon' }, '⚠️');
      const alertText = this.createElement('span', {});
      if (this.extinguisher.compliance_status === 'warning') {
        alertText.textContent = 'This extinguisher is approaching its next inspection due date.';
      } else {
        alertText.textContent = 'This extinguisher is OVERDUE for inspection. Immediate action required.';
      }
      alert.appendChild(alertIcon);
      alert.appendChild(alertText);
      section.appendChild(alert);
    }

    return section;
  }

  private createHistorySection(): HTMLElement {
    const section = this.createElement('section', { className: 'section history-section' });
    const title = this.createElement('h3', {}, `Inspection History (${this.inspections.length})`);
    section.appendChild(title);

    if (this.inspections.length === 0) {
      const empty = this.createElement('div', { className: 'empty-message' }, 'No inspection history yet');
      section.appendChild(empty);
      return section;
    }

    // Inspection timeline
    const timeline = this.createElement('div', { className: 'inspection-timeline' });

    this.inspections.forEach((inspection, index) => {
      const item = this.createElement('div', { className: 'timeline-item' });

      // Timeline dot
      const dot = this.createElement('div', {
        className: `timeline-dot ${inspection.status === 'pass' ? 'dot-pass' : 'dot-fail'}`,
      });
      item.appendChild(dot);

      // Timeline content
      const content = this.createElement('div', { className: 'timeline-content' });

      // Header
      const header = this.createElement('div', { className: 'timeline-header' });
      const date = this.createElement('div', { className: 'timeline-date' }, new Date(inspection.inspection_date).toLocaleDateString());
      const type = this.createElement('div', { className: 'timeline-type' }, inspection.inspection_type.toUpperCase());
      const statusBadge = this.createElement('span', {
        className: `inspection-badge badge-${inspection.status}`,
      }, inspection.status.toUpperCase());
      header.appendChild(date);
      header.appendChild(type);
      header.appendChild(statusBadge);
      content.appendChild(header);

      // Inspector
      const inspector = this.createElement('div', { className: 'timeline-inspector' }, `Inspector: ${inspection.inspector_name || 'Unknown'}`);
      content.appendChild(inspector);

      // Details
      const details = this.createElement('div', { className: 'timeline-details' });
      const pressure = this.createElement('div', {}, `Pressure: ${inspection.pressure_gauge_status}`);
      const seal = this.createElement('div', {}, `Seal: ${inspection.seal_intact ? 'Intact' : 'Damaged'}`);
      details.appendChild(pressure);
      details.appendChild(seal);

      if (inspection.findings) {
        const findings = this.createElement('div', { className: 'timeline-findings' }, `Findings: ${inspection.findings}`);
        details.appendChild(findings);
      }

      content.appendChild(details);

      // Photo link
      if (inspection.photo_url) {
        const photoLink = this.createElement('button', { type: 'button', className: 'photo-link' }, '📷 View photo');
        photoLink.addEventListener('click', async () => {
          try {
            photoLink.textContent = 'Loading…';
            const res = await fetch(`/api/inspections/photo/${inspection.photo_url}`, { headers: { Authorization: `Bearer ${api.getToken()}` } });
            if (!res.ok) throw new Error('Photo not available');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
          } catch (err) {
            Toast.error((err as Error).message || 'Photo not available');
          } finally {
            photoLink.textContent = '📷 View photo';
          }
        });
        content.appendChild(photoLink);
      }
      if (inspection.notes) {
        details.appendChild(this.createElement('div', { className: 'timeline-findings' }, `Notes: ${inspection.notes}`));
      }

      item.appendChild(content);
      timeline.appendChild(item);

      // Add connector line (except for last item)
      if (index < this.inspections.length - 1) {
        const connector = this.createElement('div', { className: 'timeline-connector' });
        timeline.appendChild(connector);
      }
    });

    section.appendChild(timeline);
    return section;
  }

  private createActionsSection(): HTMLElement {
    const section = this.createElement('div', { className: 'actions-section' });

    const inspectBtn = this.createElement('button', { className: 'btn-primary btn-large' }, '🔍 Perform Inspection');
    inspectBtn.addEventListener('click', () => {
      router.navigate(`/extinguishers/${this.extinguisherId}/inspect`);
    });

    const editBtn = this.createElement('button', { className: 'btn-secondary btn-large' }, '✏️ Edit Details');
    editBtn.addEventListener('click', () => {
      this.showEditModal();
    });

    const deleteBtn = this.createElement('button', { className: 'btn-danger btn-large' }, '🗑️ Remove');
    deleteBtn.addEventListener('click', () => {
      this.showDeleteModal();
    });

    section.appendChild(inspectBtn);
    section.appendChild(editBtn);
    section.appendChild(deleteBtn);

    return section;
  }

  private async loadExtinguisherData(): Promise<void> {
    try {
      // Load extinguisher details
      this.extinguisher = await api.getExtinguisher(this.extinguisherId);

      // Load inspection history
      const response = await api.getInspections({ extinguisher_id: this.extinguisherId });
      this.inspections = response.inspections.sort((a, b) => {
        return new Date(b.inspection_date).getTime() - new Date(a.inspection_date).getTime();
      });

      this.isLoading = false;
      this.shadow.innerHTML = '';
      await this.render();
    } catch (error) {
      this.isLoading = false;
      this.errorMessage = (error as Error).message || 'Failed to load extinguisher';
      this.shadow.innerHTML = '';
      await this.render();
    }
  }

  private handleLogout(): void {
    api.logout();
    router.navigate('/login');
  }

  private showEditModal(): void {
    if (!this.extinguisher) return;

    const modal = document.createElement('custom-modal') as any;
    modal.setTitle('Edit Extinguisher Details');

    // Create form
    const form = this.createElement('form', { className: 'edit-form' });
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.handleEditSubmit(e, modal);
    });

    // QR Code
    const qrGroup = this.createElement('div', { className: 'form-group' });
    const qrLabel = this.createElement('label', {}, 'QR Code');
    const qrInput = this.createElement('input', {
      type: 'text',
      name: 'qr_code',
      value: this.extinguisher.qr_code,
      required: 'true',
    }) as HTMLInputElement;
    qrGroup.appendChild(qrLabel);
    qrGroup.appendChild(qrInput);
    form.appendChild(qrGroup);

    // Type
    const typeGroup = this.createElement('div', { className: 'form-group' });
    const typeLabel = this.createElement('label', {}, 'Type');
    const typeSelect = this.createElement('select', {
      name: 'extinguisher_type',
      required: 'true',
    }) as HTMLSelectElement;
    EXTINGUISHER_TYPES.forEach(type => {
      const option = this.createElement('option', {
        value: type.value,
      }, type.label) as HTMLOptionElement;
      if (type.value === this.extinguisher!.extinguisher_type) {
        option.selected = true;
      }
      typeSelect.appendChild(option);
    });
    typeGroup.appendChild(typeLabel);
    typeGroup.appendChild(typeSelect);
    form.appendChild(typeGroup);

    // Agent
    const agentGroup = this.createElement('div', { className: 'form-group' });
    const agentLabel = this.createElement('label', {}, 'Extinguishing agent');
    const agentSelect = this.createElement('select', { name: 'agent' }) as HTMLSelectElement;
    const currentAgent = this.extinguisher.agent || '';
    EXTINGUISHER_AGENTS.forEach(a => {
      const option = this.createElement('option', { value: a.value }, a.label) as HTMLOptionElement;
      if (a.value === currentAgent) option.selected = true;
      agentSelect.appendChild(option);
    });
    agentGroup.appendChild(agentLabel);
    agentGroup.appendChild(agentSelect);
    form.appendChild(agentGroup);

    // Size
    const sizeGroup = this.createElement('div', { className: 'form-group' });
    const sizeLabel = this.createElement('label', {}, 'Size (lbs)');
    const sizeInput = this.createElement('input', {
      type: 'number',
      name: 'size_lbs',
      value: this.extinguisher.size_lbs.toString(),
      required: 'true',
      min: '1',
    }) as HTMLInputElement;
    sizeGroup.appendChild(sizeLabel);
    sizeGroup.appendChild(sizeInput);
    form.appendChild(sizeGroup);

    // Room/Area
    const roomGroup = this.createElement('div', { className: 'form-group' });
    const roomLabel = this.createElement('label', {}, 'Room/Area');
    const roomInput = this.createElement('input', {
      type: 'text',
      name: 'room_area',
      value: this.extinguisher.room_area || '',
    }) as HTMLInputElement;
    roomGroup.appendChild(roomLabel);
    roomGroup.appendChild(roomInput);
    form.appendChild(roomGroup);

    // Status
    const statusGroup = this.createElement('div', { className: 'form-group' });
    const statusLabel = this.createElement('label', {}, 'Status');
    const statusSelect = this.createElement('select', {
      name: 'status',
      required: 'true',
    }) as HTMLSelectElement;
    const statuses = [
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
      { value: 'out_of_service', label: 'Out of Service' },
    ];
    statuses.forEach(({ value, label }) => {
      const option = this.createElement('option', {
        value: value,
      }, label) as HTMLOptionElement;
      if (value === this.extinguisher!.status) {
        option.selected = true;
      }
      statusSelect.appendChild(option);
    });
    statusGroup.appendChild(statusLabel);
    statusGroup.appendChild(statusSelect);
    form.appendChild(statusGroup);

    // Buttons
    const buttonGroup = this.createElement('div', { className: 'form-buttons' });
    const cancelBtn = this.createElement('button', {
      type: 'button',
      className: 'btn-secondary',
    }, 'Cancel');
    cancelBtn.addEventListener('click', () => modal.close());
    const saveBtn = this.createElement('button', {
      type: 'submit',
      className: 'btn-primary',
    }, 'Save Changes');
    buttonGroup.appendChild(cancelBtn);
    buttonGroup.appendChild(saveBtn);
    form.appendChild(buttonGroup);

    // Add styles to modal
    const style = document.createElement('style');
    style.textContent = `
      .edit-form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
      }

      .form-group {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
      }

      .form-group label {
        font-size: 14px;
        font-weight: 600;
        color: var(--gray-700);
      }

      .form-group input,
      .form-group select {
        padding: 10px;
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 14px;
      }

      .form-group input:focus,
      .form-group select:focus {
        outline: none;
        border-color: var(--primary);
      }

      .form-buttons {
        display: flex;
        gap: var(--spacing-md);
        margin-top: var(--spacing-md);
      }

      .form-buttons button {
        flex: 1;
        padding: 12px 24px;
        border: none;
        border-radius: var(--border-radius);
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }

      .btn-primary {
        background: var(--primary);
        color: white;
      }

      .btn-primary:hover {
        background: var(--primary-dark);
      }

      .btn-secondary {
        background: white;
        color: var(--gray-700);
        border: 1px solid var(--gray-300);
      }

      .btn-secondary:hover {
        background: var(--gray-50);
      }
    `;
    form.appendChild(style);

    modal.setContent(form);
    document.body.appendChild(modal);
    modal.open();
  }

  private async handleEditSubmit(e: Event, modal: any): Promise<void> {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const data = {
      qr_code: formData.get('qr_code') as string,
      extinguisher_type: formData.get('extinguisher_type') as string,
      agent: (formData.get('agent') as string) || null,
      size_lbs: parseInt(formData.get('size_lbs') as string),
      room_area: (formData.get('room_area') as string) || undefined,
      status: formData.get('status') as string,
    };

    try {
      // Update extinguisher
      await api.updateExtinguisher(this.extinguisherId, data);

      // Close modal
      modal.close();

      // Reload data
      this.isLoading = true;
      this.shadow.innerHTML = '';
      await this.render();
    } catch (error) {
      alert((error as Error).message || 'Failed to update extinguisher');
    }
  }

  private showDeleteModal(): void {
    if (!this.extinguisher) return;

    const modal = document.createElement('custom-modal') as any;
    modal.setTitle('Delete Extinguisher');

    // Create confirmation content
    const content = this.createElement('div', { className: 'delete-confirmation' });
    const warning = this.createElement('p', {}, `Are you sure you want to delete extinguisher ${this.extinguisher.qr_code}?`);
    const message = this.createElement('p', { className: 'warning-text' }, 'This action cannot be undone. All inspection history will be permanently deleted.');
    content.appendChild(warning);
    content.appendChild(message);

    // Buttons
    const buttonGroup = this.createElement('div', { className: 'form-buttons' });
    const cancelBtn = this.createElement('button', {
      type: 'button',
      className: 'btn-secondary',
    }, 'Cancel');
    cancelBtn.addEventListener('click', () => modal.close());

    const deleteBtn = this.createElement('button', {
      type: 'button',
      className: 'btn-danger',
    }, 'Delete Permanently');
    deleteBtn.addEventListener('click', async () => {
      await this.handleDelete(modal);
    });

    buttonGroup.appendChild(cancelBtn);
    buttonGroup.appendChild(deleteBtn);
    content.appendChild(buttonGroup);

    // Add styles to modal
    const style = document.createElement('style');
    style.textContent = `
      .delete-confirmation {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
      }

      .delete-confirmation p {
        margin: 0;
        font-size: 14px;
        color: var(--gray-700);
      }

      .warning-text {
        color: var(--danger);
        font-weight: 600;
      }

      .form-buttons {
        display: flex;
        gap: var(--spacing-md);
        margin-top: var(--spacing-md);
      }

      .form-buttons button {
        flex: 1;
        padding: 12px 24px;
        border: none;
        border-radius: var(--border-radius);
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }

      .btn-danger {
        background: var(--danger);
        color: white;
      }

      .btn-danger:hover {
        background: #dc2626;
      }

      .btn-secondary {
        background: white;
        color: var(--gray-700);
        border: 1px solid var(--gray-300);
      }

      .btn-secondary:hover {
        background: var(--gray-50);
      }
    `;
    content.appendChild(style);

    modal.setContent(content);
    document.body.appendChild(modal);
    modal.open();
  }

  private async handleDelete(modal: any): Promise<void> {
    try {
      // Delete extinguisher
      await api.deleteExtinguisher(this.extinguisherId);

      // Close modal
      modal.close();

      // Navigate back to list
      router.navigate('/extinguishers');
    } catch (error) {
      alert((error as Error).message || 'Failed to delete extinguisher');
    }
  }

  private getStyles(): string {
    return `
      ${APP_NAV_STYLES}

      .detail-container {
        min-height: 100vh;
        background: var(--gray-50);
      }

      .detail-header {
        background: white;
        border-bottom: 1px solid var(--gray-200);
        padding: var(--spacing-md) var(--spacing-lg);
      }

      .header-content {
        max-width: 1200px;
        margin: 0 auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .back-btn {
        padding: 10px 20px;
        background: white;
        color: var(--gray-700);
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
      }

      .back-btn:hover {
        background: var(--gray-50);
      }

      .logo {
        font-size: 24px;
        font-weight: 700;
        color: var(--danger);
        margin: 0;
      }

      .nav {
        display: flex;
        gap: var(--spacing-md);
      }

      .btn-secondary {
        padding: 10px 20px;
        background: white;
        color: var(--gray-700);
        border: 1px solid var(--gray-300);
        border-radius: var(--border-radius);
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
      }

      .btn-secondary:hover {
        background: var(--gray-50);
      }

      .detail-main {
        max-width: 1200px;
        margin: 0 auto;
        padding: var(--spacing-lg);
      }

      .loading {
        text-align: center;
        padding: 40px;
        color: var(--gray-500);
      }

      .error-message {
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #dc2626;
        padding: var(--spacing-md);
        border-radius: var(--border-radius);
        margin-bottom: var(--spacing-md);
      }

      .section {
        background: white;
        border-radius: var(--border-radius);
        padding: var(--spacing-lg);
        margin-bottom: var(--spacing-lg);
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }

      .section-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--spacing-lg);
        padding-bottom: var(--spacing-md);
        border-bottom: 2px solid var(--gray-200);
      }

      .section-header h2 {
        font-size: 32px;
        font-weight: 700;
        color: var(--gray-900);
        margin: 0;
      }

      .status-badge {
        display: inline-block;
        padding: 8px 16px;
        border-radius: var(--border-radius);
        font-size: 14px;
        font-weight: 600;
        color: white;
      }

      .status-compliant {
        background: var(--success);
      }

      .status-warning {
        background: #f59e0b;
      }

      .status-overdue {
        background: var(--danger);
      }

      .details-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: var(--spacing-lg);
      }

      .detail-item {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
      }

      .detail-label {
        font-size: 12px;
        font-weight: 600;
        color: var(--gray-500);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .detail-value {
        font-size: 16px;
        color: var(--gray-900);
        font-weight: 500;
      }

      .compliance-alert {
        margin-top: var(--spacing-lg);
        padding: var(--spacing-md);
        border-radius: var(--border-radius);
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        font-weight: 500;
      }

      .alert-warning {
        background: #fffbeb;
        border: 1px solid #fde68a;
        color: #92400e;
      }

      .alert-overdue {
        background: #fef2f2;
        border: 1px solid #fecaca;
        color: #dc2626;
      }

      .alert-icon {
        font-size: 24px;
      }

      .history-section h3 {
        font-size: 24px;
        font-weight: 600;
        color: var(--gray-900);
        margin-bottom: var(--spacing-lg);
      }

      .empty-message {
        text-align: center;
        padding: var(--spacing-xl);
        color: var(--gray-500);
      }

      .inspection-timeline {
        position: relative;
        padding-left: 40px;
      }

      .timeline-item {
        position: relative;
        padding-bottom: var(--spacing-lg);
      }

      .timeline-dot {
        position: absolute;
        left: -40px;
        top: 0;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 3px solid white;
        box-shadow: 0 0 0 2px var(--gray-300);
      }

      .dot-pass {
        background: var(--success);
      }

      .dot-fail {
        background: var(--danger);
      }

      .timeline-connector {
        position: absolute;
        left: -33px;
        width: 2px;
        height: 100%;
        background: var(--gray-300);
      }

      .timeline-content {
        background: var(--gray-50);
        border-radius: var(--border-radius);
        padding: var(--spacing-md);
      }

      .timeline-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--spacing-sm);
        flex-wrap: wrap;
        gap: var(--spacing-sm);
      }

      .timeline-date {
        font-size: 16px;
        font-weight: 600;
        color: var(--gray-900);
      }

      .timeline-type {
        font-size: 12px;
        color: var(--gray-500);
        text-transform: uppercase;
        font-weight: 600;
      }

      .inspection-badge {
        display: inline-block;
        padding: 4px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 600;
        color: white;
      }

      .badge-pass {
        background: var(--success);
      }

      .badge-fail {
        background: var(--danger);
      }

      .timeline-inspector {
        font-size: 14px;
        color: var(--gray-700);
        margin-bottom: var(--spacing-sm);
      }

      .timeline-details {
        font-size: 14px;
        color: var(--gray-600);
        line-height: 1.6;
      }

      .timeline-findings {
        margin-top: var(--spacing-sm);
        font-style: italic;
        color: var(--gray-700);
      }

      .photo-link {
        display: inline-block;
        margin-top: var(--spacing-sm);
        color: var(--primary);
        text-decoration: none;
        font-size: 14px;
      }

      .photo-link:hover {
        text-decoration: underline;
      }

      .actions-section {
        display: flex;
        gap: var(--spacing-md);
        flex-wrap: wrap;
      }

      .btn-primary, .btn-danger {
        padding: 16px 32px;
        border: none;
        border-radius: var(--border-radius);
        font-size: 16px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s;
      }

      .btn-large {
        flex: 1;
        min-width: 200px;
      }

      .btn-primary {
        background: var(--primary);
        color: white;
      }

      .btn-primary:hover {
        background: var(--primary-dark);
      }

      .btn-danger {
        background: var(--danger);
        color: white;
      }

      .btn-danger:hover {
        background: #dc2626;
      }

      .section-header h2, .detail-value, .timeline-content { overflow-wrap: anywhere; min-width: 0; }

      @media (max-width: 768px) {
        .details-grid {
          grid-template-columns: 1fr;
        }
        .section-header { flex-wrap: wrap; gap: 8px; }
        .btn-large { min-width: 0; flex-basis: 100%; }

        .timeline-item {
          padding-left: 0;
        }

        .inspection-timeline {
          padding-left: 30px;
        }

        .timeline-dot {
          left: -30px;
        }

        .timeline-connector {
          left: -23px;
        }

        .actions-section {
          flex-direction: column;
        }

        .btn-large {
          width: 100%;
        }
      }
    `;
  }
}

// Register custom element
customElements.define('extinguisher-detail-page', ExtinguisherDetailPage);
